// core/xlsxExport.ts
// Клієнтський XLSX-експорт для звіту по оголошеннях (SheetJS/xlsx)
// Структура колонок відповідає template.xlsx

import * as XLSX from 'xlsx';
import type { AdsReportRow } from '../modules/ads-stats/useAdsStats.ts';
import { calculateReportTotals } from '../modules/ads-stats/report-totals.ts';

/**
 * Форматовані назви колонок — відповідають template.xlsx
 */
const FORMATTED_HEADERS = [
  'Ad name',
  'Currency',
  'Amount spent',
  'Impressions',
  'CPM',
  'Clicks',
  'CPC',
  'CTR (%)',
  'Leads',
  'Cost per lead',
  'App installs',
  'Cost per app install',
  'Registrations completed',
  'Cost per registration completed',
  'Purchases',
  'Cost per purchase',
  'Reporting starts',
  'Reporting ends',
];

function rowToFormatted(row: AdsReportRow, dateFrom: string, dateTo: string): unknown[] {
  return [
    row.ad_name ?? row.ad_id,
    row.currency ?? '',
    round2(row.spend),
    row.impressions,
    round2(row.cpm),
    row.clicks,
    round2(row.cpc),
    round2(row.ctr),
    row.leads,
    round2(row.cost_per_lead),
    row.app_installs,
    round2(row.cost_per_app_install),
    row.registrations,
    round2(row.cost_per_registration),
    row.purchases,
    round2(row.cost_per_purchase),
    dateFrom,
    dateTo,
  ];
}

function round2(n: number): number|null {
  return n==null?null:Math.round(n * 100) / 100;
}

/**
 * Експортувати звіт по оголошеннях у XLSX і завантажити в браузері.
 * Аркуш 1: "Formatted Report" — заголовок з датами + таблиця
 * Аркуш 2: "Raw Data Report" — TOTAL рядок першим, потім рядки даних
 */
export function exportAdsReportToXlsx(
  reports: { ads: AdsReportRow[]; geo: any[]; accounts: any[]; campaigns: any[] },
  dateFrom: string,
  dateTo: string
): void {
  const wb = XLSX.utils.book_new();
  const rows = reports.ads;

  // ── Аркуш 1: Formatted Report ───────────────────────────────
  const ws1Data: any[][] = [];

  // Заголовок з датами
  ws1Data.push([`Report Period: ${dateFrom} - ${dateTo}`]);
  ws1Data.push([]); // порожній рядок
  ws1Data.push(FORMATTED_HEADERS);

  for (const row of rows) {
    ws1Data.push(rowToFormatted(row, dateFrom, dateTo));
  }

  const ws1 = XLSX.utils.aoa_to_sheet(ws1Data);

  // Ширина колонок
  ws1['!cols'] = [
    { wch: 40 }, // Ad name
    { wch: 8 },  // Currency
    { wch: 14 }, // Amount spent
    { wch: 12 }, // Impressions
    { wch: 10 }, // CPM
    { wch: 10 }, // Clicks
    { wch: 10 }, // CPC
    { wch: 8 },  // CTR
    { wch: 8 },  // Leads
    { wch: 14 }, // Cost per lead
    { wch: 12 }, // App installs
    { wch: 20 }, // Cost per app install
    { wch: 22 }, // Registrations
    { wch: 30 }, // Cost per reg
    { wch: 10 }, // Purchases
    { wch: 16 }, // Cost per purchase
    { wch: 14 }, // Date from
    { wch: 14 }, // Date to
  ];

  XLSX.utils.book_append_sheet(wb, ws1, 'Formatted Report');

  // ── Аркуш 2: Raw Data Report (TOTAL першим) ─────────────────
  const ws2Data: unknown[][] = [];
  ws2Data.push(FORMATTED_HEADERS);

  // Підсумковий TOTAL-рядок (перший)
  const totalSpend = rows.reduce((s, r) => s + r.spend, 0);
  const totalImpr = rows.reduce((s, r) => s + r.impressions, 0);
  const totalClicks = rows.reduce((s, r) => s + r.clicks, 0);
  const totalLeads = rows.reduce((s, r) => s + r.leads, 0);
  const totalAppInstalls = rows.reduce((s, r) => s + r.app_installs, 0);
  const totalRegs = rows.reduce((s, r) => s + r.registrations, 0);
  const totalPurchases = rows.reduce((s, r) => s + r.purchases, 0);

  ws2Data.push([
    'TOTAL',
    rows[0]?.currency ?? '',
    round2(totalSpend),
    totalImpr,
    round2(totalImpr > 0 ? (totalSpend / totalImpr) * 1000 : 0),
    totalClicks,
    round2(totalClicks > 0 ? totalSpend / totalClicks : 0),
    round2(totalImpr > 0 ? (totalClicks / totalImpr) * 100 : 0),
    totalLeads,
    round2(totalLeads > 0 ? totalSpend / totalLeads : 0),
    totalAppInstalls,
    round2(totalAppInstalls > 0 ? totalSpend / totalAppInstalls : 0),
    totalRegs,
    round2(totalRegs > 0 ? totalSpend / totalRegs : 0),
    totalPurchases,
    round2(totalPurchases > 0 ? totalSpend / totalPurchases : 0),
    dateFrom,
    dateTo,
  ]);

  for (const row of rows) {
    ws2Data.push(rowToFormatted(row, dateFrom, dateTo));
  }
  const summary=calculateReportTotals(rows);
  if(summary.incomplete)ws2Data[1][0]='TOTAL (incomplete)';
  if(!summary.hasData)for(let i=2;i<=15;i++)ws2Data[1][i]=null;

  const ws2 = XLSX.utils.aoa_to_sheet(ws2Data);
  ws2['!cols'] = ws1['!cols'];

  XLSX.utils.book_append_sheet(wb, ws2, 'Raw Data Report');

  // ── Аркуш 3: По GEO ──────────────────────────────────────────
  const wsGeoData: any[][] = [['GEO', 'Cost']];
  const geoSorted = [...reports.geo].sort((a, b) => b.spend - a.spend);
  geoSorted.forEach(r => wsGeoData.push([r.country, round2(r.spend)]));
  // Total row
  wsGeoData.push(['Total', { t: 'n', f: `SUM(B2:B${geoSorted.length + 1})` }]);
  const wsGeo = XLSX.utils.aoa_to_sheet(wsGeoData);
  wsGeo['!cols'] = [{ wch: 15 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(wb, wsGeo, 'По GEO');

  // ── Аркуш 4: По кабінетах ─────────────────────────────────────
  const wsAccData: any[][] = [['Account ID', 'Cost']];
  const accSorted = [...reports.accounts].sort((a, b) => b.spend - a.spend);
  accSorted.forEach(r => wsAccData.push([r.ad_account_id, round2(r.spend)]));
  // Total row
  const accountSummary=calculateReportTotals(accSorted);
  wsAccData.push([accountSummary.incomplete?'Total (incomplete)':'Total',accountSummary.hasData?{ t: 'n', f: `SUM(B2:B${accSorted.length + 1})` }:null]);
  const wsAcc = XLSX.utils.aoa_to_sheet(wsAccData);
  wsAcc['!cols'] = [{ wch: 20 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(wb, wsAcc, 'По кабінетах');

  // ── Аркуш 5: По кампаніях ─────────────────────────────────────
  const wsCampData: any[][] = [['Campaign Name', 'Cost']];
  const campSorted = [...reports.campaigns].sort((a, b) => b.spend - a.spend);
  campSorted.forEach(r => wsCampData.push([r.campaign_name, round2(r.spend)]));
  // Total row
  const campaignSummary=calculateReportTotals(campSorted);
  wsCampData.push([campaignSummary.incomplete?'Total (incomplete)':'Total',campaignSummary.hasData?{ t: 'n', f: `SUM(B2:B${campSorted.length + 1})` }:null]);
  const wsCamp = XLSX.utils.aoa_to_sheet(wsCampData);
  wsCamp['!cols'] = [{ wch: 60 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(wb, wsCamp, 'По кампаніях');

  // ── Завантаження ─────────────────────────────────────────────
  const filename = `ads_report_${dateFrom}_${dateTo}.xlsx`;
  XLSX.writeFile(wb, filename);
}
